import { forwardRef, useImperativeHandle, useState } from 'react';
import { Modal } from 'antd';
import styled from 'styled-components';
import { IExitsAppointmentData } from '../services/types/booking';
import moment from 'moment';

type Props = {};

export type ContinueBookingModalRef = {
  open: (data: IExitsAppointmentData) => void;
};

const ContinueBookingModal = forwardRef<ContinueBookingModalRef, Props>((_, ref) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<IExitsAppointmentData | null>(null);

  useImperativeHandle(ref, () => ({
    open: (data: IExitsAppointmentData) => {
      setOpen(true);
      setData(data);
    },
  }));

  const handleCancel = () => {
    setOpen(false);
  };

  const handleContinueBooking = () => {
    setOpen(false);
  };

  const date = data?.startTime ? moment(data.startTime).format('MM-DD-YYYY') : '';
  const startTime = data?.startTime ? moment(data.startTime, 'MM-DD-YYYY HH:mm:ss').format('hh:mm A') : '';
  const endTime = data?.endTime ? moment(data.endTime, 'MM-DD-YYYY HH:mm:ss').format('hh:mm A') : '';


  return (
    <ModalStyled
      open={open}
      onCancel={handleCancel}
      width={444}
      destroyOnClose
      centered
      closable={false}
      footer={null}
      title={null}
      bodyStyle={{ padding: 0 }}
    >
      <Container className='Container'>
        <HeaderIconBox className='HeaderIconBox'>
          <IcNoti />
        </HeaderIconBox>
        <ContentContainer className='ContentContainer'>
          <ContentBox>
            <HeaderTitle>You Already Have a Booking</HeaderTitle>
            <HeaderDescription>It looks like you have already made a booking.</HeaderDescription>
            <div className='details-box'>
              <DetailsText>Details of your current appointment:</DetailsText>
              <div className='calendar-box'>
                <div className='date-time-row'>
                  <div className='date-item'>
                    <IconCalendar />
                    <span>{date}</span>
                  </div>
                  <div className='divider'></div>
                  <div className='time-item'>
                    <IconClock />
                    <span>
                      {startTime} - {endTime}
                    </span>
                  </div>
                </div>
                <div className='customer-service-row'>
                  {data?.services.map((service) => (
                    <div className='customer-item' key={service.serviceId}>
                      <div>{data.staffName || 'Unknown'}:</div>
                      <div>{service.serviceName}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <FooterDescription>
              If you proceed, you will create an overlapping booking with your existing appointment.
            </FooterDescription>
          </ContentBox>
        </ContentContainer>
        <FooterContainer className='FooterContainer'>
          <StyledButton onClick={handleCancel}>CANCEL</StyledButton>
          <StyledButton className='continue-booking' onClick={handleContinueBooking}>
            CONTINUE
          </StyledButton>
        </FooterContainer>
      </Container>
    </ModalStyled>
  );
});

ContinueBookingModal.displayName = 'ContinueBookingModal';

export default ContinueBookingModal;

const ModalStyled = styled(Modal)`
  .ant-modal-content {
    border-radius: 32px;
  }
  @media only screen and (max-width: 768px) {
    // zoom: 0.9;
    .HeaderIconBox {
      zoom: 0.8;
    }
    .Container {
      zoom: 0.9;
    }
  }
`;

const Container = styled.div`
  width: 100%;
  height: 100%;
  padding-top: 45px;
  color: #1d2129;
  font-style: normal;
  line-height: normal;
  font-family: Poppins;
  font-weight: 400;
`;

const HeaderIconBox = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -70%);
  display: flex;
  width: 133px;
  height: 132px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  aspect-ratio: 133/132;

  border-radius: 50%;
  background: #fff;
  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
`;

const ContentContainer = styled.div`
  display: flex;
  padding: 0 16px 8px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const ContentBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  align-self: stretch;
  .details-box {
    display: flex;
    padding: 16px 0 8px 0;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 4px;
    align-self: stretch;
    .calendar-box {
      display: flex;
      padding: 8px;
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
      align-self: stretch;
      border-radius: 4px;
      background: #f6f7fc;
      .divider {
        width: 1px;
        border-right: 1px solid #ccd4dc;
        height: 21px;
      }
      .date-time-row {
        display: flex;
        align-items: center;
        gap: 8px;
        align-self: stretch;

        .date-item,
        .time-item {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #1d2129;
          font-family: Poppins;
          font-size: 14px;
          font-style: normal;
          font-weight: 400;
          line-height: normal;
        }
      }

      .customer-service-row {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
        padding: 8px;
        align-self: stretch;
        border-radius: 4px;
        background: #fff;

        .customer-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          align-self: stretch;
          gap: 4px;
          color: #1d2129;
          font-family: Poppins;
          font-size: 14px;
          font-style: normal;
          font-weight: 500;
          line-height: normal;
        }
      }
    }
  }
`;

const FooterContainer = styled.div`
  border-top: 1px solid #ccd4dc;
  display: flex;
  padding: 12px 16px 24px;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const StyledButton = styled.button`
  display: flex;
  height: 48px;
  padding: 16px 24px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  flex: 1;
  border-radius: 8px;
  background: #e5e6eb;
  color: #1d2129;
  text-align: center;
  font-family: Poppins;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  &.continue-booking {
    flex: 1;
    background: #2463eb;
    color: #fff;
  }
`;

const HeaderTitle = styled.div`
  text-align: center;
  font-size: 20px;
  font-weight: 500;
`;

const HeaderDescription = styled.div`
  color: #505050;
  text-align: center;
  font-size: 12px;
`;

const DetailsText = styled.div`
  text-align: center;
  font-size: 14px;
`;

const FooterDescription = styled.div`
  padding: 0 16px;
  color: #ff7d00;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
`;

const IcNoti = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='57' height='64' viewBox='0 0 57 64' fill='none'>
    <path
      d='M47.5001 32.8577V26.875C47.5001 18.1473 41.6988 10.7499 33.7501 8.33375V5.625C33.7501 2.52338 31.2268 0 28.1251 0C25.0235 0 22.5001 2.52338 22.5001 5.625V8.33375C14.5514 10.7499 8.75015 18.1471 8.75015 26.875V32.8577C8.75015 40.5243 5.8279 47.7938 0.521771 53.3273C0.00177109 53.8695 -0.144354 54.6696 0.150396 55.3606C0.445146 56.0516 1.1239 56.5 1.87515 56.5H18.9388C19.8098 60.774 23.5978 64 28.1251 64C32.6526 64 36.4404 60.774 37.3115 56.5H54.3751C55.1264 56.5 55.805 56.0516 56.0998 55.3606C56.3945 54.6696 56.2484 53.8695 55.7285 53.3273C50.4224 47.7938 47.5001 40.5241 47.5001 32.8577ZM26.2501 5.625C26.2501 4.59113 27.0913 3.75 28.1251 3.75C29.159 3.75 30.0001 4.59113 30.0001 5.625V7.591C29.383 7.5315 28.7576 7.5 28.1251 7.5C27.4926 7.5 26.8673 7.5315 26.2501 7.591V5.625ZM28.1251 60.25C25.6808 60.25 23.5966 58.6824 22.8226 56.5H33.4276C32.6536 58.6824 30.5695 60.25 28.1251 60.25ZM5.9164 52.75C10.1865 47.026 12.5001 40.099 12.5001 32.8577V26.875C12.5001 18.2594 19.5095 11.25 28.1251 11.25C36.7408 11.25 43.7501 18.2594 43.7501 26.875V32.8577C43.7501 40.099 46.0638 47.026 50.334 52.75H5.9164Z'
      fill='#F44238'
    />
    <path
      d='M52.5002 26.8749C52.5002 27.9104 53.3397 28.7499 54.3752 28.7499C55.4107 28.7499 56.2502 27.9104 56.2502 26.8749C56.2502 19.3624 53.3247 12.2995 48.0126 6.98737C47.2805 6.25525 46.0932 6.25512 45.361 6.98737C44.6287 7.71962 44.6287 8.90675 45.361 9.639C49.9648 14.2429 52.5002 20.364 52.5002 26.8749Z'
      fill='#F44238'
    />
    <path
      d='M1.87512 28.75C2.91062 28.75 3.75012 27.9105 3.75012 26.875C3.75012 20.3643 6.28562 14.2432 10.8894 9.63929C11.6216 8.90704 11.6216 7.71991 10.8894 6.98766C10.1572 6.25541 8.97 6.25541 8.23775 6.98766C2.92562 12.2998 0.00012207 19.3625 0.00012207 26.875C0.00012207 27.9105 0.839622 28.75 1.87512 28.75Z'
      fill='#F44238'
    />
  </svg>
);

const IconClock = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='21' height='20' viewBox='0 0 21 20' fill='none'>
    <path
      d='M10.9688 18.75C9.23817 18.75 7.54644 18.2368 6.10751 17.2754C4.66858 16.3139 3.54707 14.9473 2.88481 13.3485C2.22254 11.7496 2.04926 9.9903 2.38688 8.29296C2.7245 6.59563 3.55786 5.03653 4.78157 3.81282C6.00528 2.58911 7.56438 1.75575 9.26171 1.41813C10.959 1.08051 12.7184 1.25379 14.3172 1.91606C15.9161 2.57832 17.2827 3.69983 18.2441 5.13876C19.2056 6.57769 19.7188 8.26942 19.7188 10C19.7188 12.3206 18.7969 14.5462 17.1559 16.1872C15.515 17.8281 13.2894 18.75 10.9688 18.75ZM10.9688 2.5C9.48539 2.5 8.03535 2.93987 6.80198 3.76398C5.56861 4.58809 4.60731 5.75943 4.03966 7.12988C3.472 8.50032 3.32348 10.0083 3.61286 11.4632C3.90225 12.918 4.61656 14.2544 5.66545 15.3033C6.71435 16.3522 8.05072 17.0665 9.50558 17.3559C10.9604 17.6453 12.4684 17.4968 13.8389 16.9291C15.2093 16.3614 16.3807 15.4002 17.2048 14.1668C18.0289 12.9334 18.4688 11.4834 18.4688 10C18.4688 8.01088 17.6786 6.10323 16.2721 4.6967C14.8655 3.29018 12.9579 2.5 10.9688 2.5Z'
      fill='#1D2129'
    />
    <path d='M13.8375 13.75L10.3438 10.2563V4.375H11.5938V9.7375L14.7188 12.8688L13.8375 13.75Z' fill='#1D2129' />
  </svg>
);
const IconCalendar = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'>
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M3.75 3.75C3.05964 3.75 2.5 4.30964 2.5 5V16.25C2.5 16.9404 3.05964 17.5 3.75 17.5H16.25C16.9404 17.5 17.5 16.9404 17.5 16.25V5C17.5 4.30964 16.9404 3.75 16.25 3.75H3.75ZM1.25 5C1.25 3.61929 2.36929 2.5 3.75 2.5H16.25C17.6307 2.5 18.75 3.61929 18.75 5V16.25C18.75 17.6307 17.6307 18.75 16.25 18.75H3.75C2.36929 18.75 1.25 17.6307 1.25 16.25V5Z'
      fill='#1D2129'
    />
    <path
      d='M11.5625 10C12.0803 10 12.5 9.58027 12.5 9.0625C12.5 8.54473 12.0803 8.125 11.5625 8.125C11.0447 8.125 10.625 8.54473 10.625 9.0625C10.625 9.58027 11.0447 10 11.5625 10Z'
      fill='#1D2129'
    />
    <path
      d='M14.6875 10C15.2053 10 15.625 9.58027 15.625 9.0625C15.625 8.54473 15.2053 8.125 14.6875 8.125C14.1697 8.125 13.75 8.54473 13.75 9.0625C13.75 9.58027 14.1697 10 14.6875 10Z'
      fill='#1D2129'
    />
    <path
      d='M11.5625 13.125C12.0803 13.125 12.5 12.7053 12.5 12.1875C12.5 11.6697 12.0803 11.25 11.5625 11.25C11.0447 11.25 10.625 11.6697 10.625 12.1875C10.625 12.7053 11.0447 13.125 11.5625 13.125Z'
      fill='#1D2129'
    />
    <path
      d='M14.6875 13.125C15.2053 13.125 15.625 12.7053 15.625 12.1875C15.625 11.6697 15.2053 11.25 14.6875 11.25C14.1697 11.25 13.75 11.6697 13.75 12.1875C13.75 12.7053 14.1697 13.125 14.6875 13.125Z'
      fill='#1D2129'
    />
    <path
      d='M5.3125 13.125C5.83027 13.125 6.25 12.7053 6.25 12.1875C6.25 11.6697 5.83027 11.25 5.3125 11.25C4.79473 11.25 4.375 11.6697 4.375 12.1875C4.375 12.7053 4.79473 13.125 5.3125 13.125Z'
      fill='#1D2129'
    />
    <path
      d='M8.4375 13.125C8.95527 13.125 9.375 12.7053 9.375 12.1875C9.375 11.6697 8.95527 11.25 8.4375 11.25C7.91973 11.25 7.5 11.6697 7.5 12.1875C7.5 12.7053 7.91973 13.125 8.4375 13.125Z'
      fill='#1D2129'
    />
    <path
      d='M5.3125 16.25C5.83027 16.25 6.25 15.8303 6.25 15.3125C6.25 14.7947 5.83027 14.375 5.3125 14.375C4.79473 14.375 4.375 14.7947 4.375 15.3125C4.375 15.8303 4.79473 16.25 5.3125 16.25Z'
      fill='#1D2129'
    />
    <path
      d='M8.4375 16.25C8.95527 16.25 9.375 15.8303 9.375 15.3125C9.375 14.7947 8.95527 14.375 8.4375 14.375C7.91973 14.375 7.5 14.7947 7.5 15.3125C7.5 15.8303 7.91973 16.25 8.4375 16.25Z'
      fill='#1D2129'
    />
    <path
      d='M11.5625 16.25C12.0803 16.25 12.5 15.8303 12.5 15.3125C12.5 14.7947 12.0803 14.375 11.5625 14.375C11.0447 14.375 10.625 14.7947 10.625 15.3125C10.625 15.8303 11.0447 16.25 11.5625 16.25Z'
      fill='#1D2129'
    />
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M5 1.25C5.34518 1.25 5.625 1.52982 5.625 1.875V3.125C5.625 3.47018 5.34518 3.75 5 3.75C4.65482 3.75 4.375 3.47018 4.375 3.125V1.875C4.375 1.52982 4.65482 1.25 5 1.25Z'
      fill='#1D2129'
    />
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M15 1.25C15.3452 1.25 15.625 1.52982 15.625 1.875V3.125C15.625 3.47018 15.3452 3.75 15 3.75C14.6548 3.75 14.375 3.47018 14.375 3.125V1.875C14.375 1.52982 14.6548 1.25 15 1.25Z'
      fill='#1D2129'
    />
    <path fillRule='evenodd' clipRule='evenodd' d='M1.875 5.625H18.125V6.875H1.875V5.625Z' fill='#1D2129' />
  </svg>
);
